import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import PageHeader from './_components/PageHeader';
/* ---------- Icons ---------- */

const I = ({ d, className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path d={d} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BellIcon     = (p) => <I {...p} d="M12 3a5 5 0 00-5 5v3.5L5.5 15h13L17 11.5V8a5 5 0 00-5-5zM10 18a2 2 0 004 0" />;
const SearchIcon   = (p) => <I {...p} d="M11 4a7 7 0 100 14 7 7 0 000-14zM20 20l-3.5-3.5" />;
const ChatIcon     = (p) => <I {...p} d="M4 5h16v11H8l-4 4V5z" />;
const MoreIcon     = (p) => <I {...p} d="M6 12h.01M12 12h.01M18 12h.01" />;
const ChevronRight = (p) => <I {...p} d="M9 6l6 6-6 6" />;
const CheckIcon    = (p) => <I {...p} d="M5 12l4 4 10-10" />;
const PlusIcon     = (p) => <I {...p} d="M12 5v14M5 12h14" />;
const TransferIcon = (p) => <I {...p} d="M7 10l4-4 4 4M17 14l-4 4-4-4" />;
const DepositIcon  = (p) => <I {...p} d="M7 14l4 4 4-4M17 10l-4-4-4 4" />;
const BillIcon     = (p) => <I {...p} d="M6 3h12v18l-3-2-3 2-3-2-3 2V3zM9 8h6M9 12h6" />;
const PersonIcon   = (p) => <I {...p} d="M12 4a4 4 0 100 8 4 4 0 000-8zM4 20a8 8 0 0116 0" />;
const MessageIcon  = (p) => <I {...p} d="M4 5h16v11H8l-4 4V5z" />;
const PhoneIcon    = (p) => <I {...p} d="M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a1 1 0 01-1 1A16 16 0 014 5a1 1 0 011-1z" />;
const ArrowUpRight = (p) => <I {...p} d="M7 17L17 7M9 7h8v8" />;
const ArrowDownLeft = (p) => <I {...p} d="M17 7L7 17M15 17H7V9" />;

/* ---------- Helpers ---------- */

const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDateLong = (d = new Date()) =>
  d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

const fmtDateShort = (d) => {
  if (!d) return '';
  const date = new Date(d);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const maskAccount = (num) => (num ? `•••${String(num).slice(-4)}` : '•••----');

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

/* ---------- Small building blocks ---------- */

const PillButton = ({ icon, label }) => (
  <button className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 text-[13px] font-medium text-[#0b2740] shrink-0 hover:bg-gray-50">
    {icon}
    {label}
  </button>
);

const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm ${className}`}>
    {children}
  </div>
);

const SectionLabel = ({ children }) => (
  <div className="text-[11px] tracking-[0.18em] uppercase text-gray-500 mb-3">
    {children}
  </div>
);

/* ---------- Page ---------- */

const FintechDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.get('/fintech/dashboard');
        if (!alive) return;
        setData(res.data);

        // Persist display name for the layout (drawer / top bar)
        if (res.data.accountHolderName) {
          localStorage.setItem('fintech_name', res.data.accountHolderName);
          window.dispatchEvent(new Event('fintech-profile-updated'));
        }
        if (res.data.caseNumber) {
          localStorage.setItem('fintech_caseNumber', res.data.caseNumber);
          window.dispatchEvent(new Event('fintech-profile-updated'));
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading dashboard…</div>;
  }

  if (!data) {
    return (
      <div className="p-6 text-sm text-red-600">
        Could not load your dashboard. Please try signing in again.
      </div>
    );
  }

  /* ---- Derived display values ---- */

  const accounts = data.linkedAccounts || [];
  const primary = accounts[0] || {};
  const accountCount = accounts.length || 1;

  const totalBalance = data.totalBalance ?? 0;
  const accountType = primary.accountType ?? 'Checking';
  const accountNumber = primary.accountNumberMasked ?? '•••----';
  const accountStatus = primary.status ?? 'active';

  const holderName = data.accountHolderName || '';
  const greetingLine = holderName ? `${greeting()}, ${holderName}` : greeting();

  // Transactions: use real data if present, otherwise fall back to a
  // single placeholder row so the visual matches the design.
  const rawTx = data.recentTransactions || [];
  const transactions =
    rawTx.length > 0
      ? rawTx.map((tx) => ({
          id: tx.trackingNumber,
          name: tx.name || 'Transfer',
          date: tx.paymentDate,
          amount: tx.amountPaid || 0,
          direction: (tx.amountPaid || 0) >= 0 ? 'in' : 'out',
          status: tx.status || 'Pending',
        }))
      : [
          {
            id: 'placeholder',
            name: 'NFCU-FDIC',
            date: new Date(),
            amount: totalBalance,
            direction: 'in',
            status: 'Pending',
          },
        ];

  return (
    <div className="px-4 pt-5 pb-8 lg:px-8 lg:pt-8 max-w-6xl mx-auto">
      {/* Greeting */}
      <div className="mb-4">
        <h1 className="font-serif text-[26px] leading-tight text-[#1a2b3c]">
          {greetingLine}
        </h1>
        <p className="text-[13px] text-gray-500 mt-1">{fmtDateLong()}</p>
      </div>

      {/* Secure session + bell */}
      <div className="flex items-center justify-between mb-5">
        <span className="inline-flex items-center gap-2 bg-[#e8f3ec] text-[#1f5c3a] text-[12px] font-medium px-3 py-1.5 rounded-full">
          <CheckIcon className="w-3.5 h-3.5" />
          Secure session
        </span>
        <button
          aria-label="Notifications"
          className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-[#0b2740] hover:bg-gray-50"
        >
          <BellIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Search + chat */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1 flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2.5">
          <SearchIcon className="w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search accounts, transactions, and tools"
            className="flex-1 bg-transparent text-[13px] text-gray-700 placeholder:text-gray-400 focus:outline-none"
          />
        </div>
        <button
          aria-label="Chat"
          className="w-11 h-11 rounded-full bg-[#0b2740] text-white flex items-center justify-center hover:bg-[#12324f]"
        >
          <ChatIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Accounts header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[15px] font-semibold text-[#1a2b3c]">Accounts</h2>
        <button
          aria-label="More"
          className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center text-[#0b2740] hover:bg-gray-50"
          onClick={() => navigate('/fintech/dashboard/profile')}
       >
          <MoreIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Accounts card */}
      <Card className="overflow-hidden mb-8">
        <div className="bg-[#0b2740] text-white px-5 py-3 flex items-center justify-between">
          <span className="text-[13px] font-medium">Bank accounts ({accountCount})</span>
          <span className="text-[13px] font-semibold">{fmtMoney(totalBalance)}</span>
        </div>

        <div className="px-5 py-5">
          <button
            onClick={() => navigate('/dashboard/accounts')}
            className="w-full flex items-center justify-between text-left"
          >
            <span className="text-[14px] font-semibold text-[#1a2b3c] tracking-wide">
              {accountType.toUpperCase()} ({maskAccount(accountNumber)})
            </span>
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </button>

          <div className="mt-2">
            <span className="inline-block bg-gray-100 text-gray-600 text-[11px] px-2 py-0.5 rounded-full capitalize">
              {accountStatus}
            </span>
          </div>

          <div className="mt-5">
            <div className="text-[26px] font-bold text-[#1a2b3c] leading-none">
              {fmtMoney(totalBalance)}
            </div>
            <div className="text-[12px] text-gray-500 mt-1">Available balance</div>
          </div>
        </div>

        <Link
          to="/dashboard/accounts"
          className="bg-[#0b2740] text-white px-5 py-3 flex items-center justify-between text-[13px] font-medium"
        >
          <span>View all accounts</span>
          <ChevronRight className="w-4 h-4" />
        </Link>
      </Card>

      {/* Quick actions — row 1 */}
      <div className="mb-8">
        <SectionLabel>Quick actions</SectionLabel>
        <div className="flex items-center gap-3 overflow-x-auto pb-1">
          <button
            aria-label="New"
            className="w-11 h-11 rounded-full bg-[#0b2740] text-white flex items-center justify-center shrink-0 hover:bg-[#12324f]"
          >
            <PlusIcon className="w-5 h-5" />
          </button>
          <PillButton icon={<TransferIcon className="w-4 h-4" />} label="Transfer" />
          <PillButton icon={<DepositIcon className="w-4 h-4" />} label="Deposit" />
        </div>

        {/* Quick actions — row 2 */}
        <div className="flex items-center gap-3 overflow-x-auto pb-1 mt-3">
          <PillButton icon={<BillIcon className="w-4 h-4" />}    label="Pay a bill" />
          <PillButton icon={<PersonIcon className="w-4 h-4" />}  label="Pay a person" />
          <PillButton icon={<MessageIcon className="w-4 h-4" />} label="Message" />
        </div>
      </div>

      {/* Transactions */}
      <Card className="mb-8 overflow-hidden">
        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
          <h3 className="text-[16px] font-semibold text-[#1a2b3c]">Transactions</h3>
        </div>
        <Link
          to="/dashboard/transactions"
          className="px-5 inline-block text-[13px] font-medium text-[#1a4b8c] hover:underline mb-3"
        >
          See more
        </Link>

        <ul className="border-t border-gray-100">
          {transactions.map((tx) => (
            <li
              key={tx.id}
              className="px-5 py-4 flex items-center gap-4 border-b border-gray-100 last:border-b-0"
            >
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                  tx.direction === 'in'
                    ? 'bg-[#e8f3ec] text-[#1f5c3a]'
                    : 'bg-red-50 text-red-600'
                }`}
              >
                {tx.direction === 'in' ? (
                  <ArrowUpRight className="w-4 h-4" />
                ) : (
                  <ArrowDownLeft className="w-4 h-4" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold text-[#1a2b3c] truncate">
                  {tx.name}
                </div>
                <div className="text-[12px] text-gray-500 mt-0.5">
                  {fmtDateShort(tx.date)}
                  {primary.accountNumberMasked
                    ? ` · ${primary.accountNumberMasked.replace(/[^0-9]/g, '').slice(-4)}`
                    : ''}
                </div>
              </div>

              <div className="text-right shrink-0">
                <div
                  className={`text-[14px] font-bold ${
                    tx.direction === 'in' ? 'text-[#1f5c3a]' : 'text-red-600'
                  }`}
                >
                  {tx.direction === 'in' ? '+' : '-'}
                  {fmtMoney(Math.abs(tx.amount))}
                </div>
                {tx.status && (
                  <span className="inline-block mt-1 bg-gray-100 text-gray-600 text-[10px] px-2 py-0.5 rounded-full">
                    {tx.status}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </Card>

      {/* Payments */}
      <Card className="mb-8">
        <div className="px-5 pt-5 pb-3">
          <h3 className="text-[16px] font-semibold text-[#1a2b3c]">Payments</h3>
        </div>

        <ul>
          <li className="border-t border-gray-100">
            <button className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50">
              <span className="w-10 h-10 rounded-full bg-[#0b2740] text-white flex items-center justify-center shrink-0">
                <BillIcon className="w-5 h-5" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[14px] font-semibold text-[#1a2b3c]">
                  Pay a bill
                </span>
                <span className="block text-[12px] text-gray-500 mt-0.5">
                  Schedule or send a payment
                </span>
              </span>
              <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
            </button>
          </li>
          <li className="border-t border-gray-100">
            <button className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50">
              <span className="w-10 h-10 rounded-full bg-[#0b2740] text-white flex items-center justify-center shrink-0">
                <PersonIcon className="w-5 h-5" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[14px] font-semibold text-[#1a2b3c]">
                  Pay a person
                </span>
                <span className="block text-[12px] text-gray-500 mt-0.5">
                  Send money to someone you trust
                </span>
              </span>
              <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
            </button>
          </li>
        </ul>
      </Card>

      {/* Support */}
      <Card className="mb-8">
        <div className="px-5 pt-5 pb-3">
          <h3 className="text-[16px] font-semibold text-[#1a2b3c]">Support</h3>
          <div className="text-[11px] tracking-[0.18em] uppercase text-gray-500 mt-1">
            Dedicated support
          </div>
        </div>

        <ul>
          <li className="border-t border-gray-100">
            <a
              href="tel:+15550100100"
              className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50"
            >
              <span className="w-10 h-10 rounded-full bg-gray-100 text-[#1a2b3c] flex items-center justify-center shrink-0">
                <PhoneIcon className="w-5 h-5" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[14px] font-semibold text-[#1a2b3c]">
                  Call
                </span>
                <span className="block text-[12px] text-gray-500 mt-0.5">
                  +1 (555) 010-0100
                </span>
              </span>
              <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
            </a>
          </li>
          <li className="border-t border-gray-100">
            <button className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50">
              <span className="w-10 h-10 rounded-full bg-gray-100 text-[#1a2b3c] flex items-center justify-center shrink-0">
                <MessageIcon className="w-5 h-5" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[14px] font-semibold text-[#1a2b3c]">
                  Message
                </span>
                <span className="block text-[12px] text-gray-500 mt-0.5">
                  Secure inbox · typical hours 9:00–6:00
                </span>
              </span>
              <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
            </button>
          </li>
        </ul>
      </Card>

      {/* More for you */}
      <Card className="mb-4">
        <div className="p-5">
          <h3 className="text-[16px] font-semibold text-[#1a2b3c]">More for you</h3>
          <p className="mt-2 text-[13px] leading-6 text-gray-600">
            Explore card delivery, representative tools, and secure messaging
            from the menu anytime.
          </p>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <Link
              to="/dashboard/card-delivery"
              className="inline-flex items-center justify-center bg-white border border-gray-300 text-[#0b2740] text-[13px] font-medium px-4 py-2 rounded-full hover:bg-gray-50"
            >
              Card delivery
            </Link>
            <Link
              to="/dashboard/transactions"
              className="inline-flex items-center justify-center bg-white border border-gray-300 text-[#0b2740] text-[13px] font-medium px-4 py-2 rounded-full hover:bg-gray-50"
            >
              Activity
            </Link>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default FintechDashboard;